// Servicio de competencias sincronizadas desde LiftingCast (panel admin)
import API_CONFIG from '../config/api';
import { apiGet, apiPost, apiPut, apiDelete } from './api';

/**
 * Obtiene el id del meet desde lo que pegue el usuario.
 * Acepta el id suelto, /results, /platforms/.../display y archive.liftingcast.com.
 * @returns {string|null} el id, o null si no se reconoce
 */
export const extraerMeetId = (entrada) => {
  const texto = (entrada || '').trim();
  if (!texto) return null;

  const desdeUrl = texto.match(/\/meets\/([a-zA-Z0-9]+)/);
  const candidato = desdeUrl ? desdeUrl[1] : texto;

  return /^[a-zA-Z0-9]{6,40}$/.test(candidato) ? candidato : null;
};

/**
 * Deja la URL siempre como https://<host>/meets/<id>/results.
 * Conserva archive.liftingcast.com si venía en el link pegado.
 */
export const normalizarUrlLiftingCast = (entrada) => {
  const meetId = extraerMeetId(entrada);
  if (!meetId) return null;

  let host = 'liftingcast.com';
  const texto = (entrada || '').trim();
  const conEsquema = texto.includes('://') ? texto : (texto.includes('liftingcast.com') ? `https://${texto}` : texto);
  try {
    const url = new URL(conEsquema);
    if (url.hostname === 'archive.liftingcast.com') {
      host = 'archive.liftingcast.com';
    } else if (url.hostname === 'liftingcast.com' || url.hostname === 'www.liftingcast.com') {
      host = 'liftingcast.com';
    }
  } catch {
    /* id suelto: se usa liftingcast.com */
  }

  return `https://${host}/meets/${meetId}/results`;
};

const leerJson = async (response) => {
  const data = await response.json();
  if (data && data.success === false) {
    throw new Error(data.error || 'Error inesperado del servidor');
  }
  return data;
};

export const getMeets = async () => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.LIFTINGCAST.MEETS);
  const data = await leerJson(response);
  return data.items || [];
};

export const getMeet = async (meetId) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.LIFTINGCAST.MEET(meetId));
  const data = await leerJson(response);
  return data.data || null;
};

const textoEsTested = (texto) => {
  const t = (texto || '').toLowerCase();
  return t.includes('tested') && !t.includes('untested');
};

const atletaEsTested = (atleta) => {
  if (textoEsTested(atleta.division_nombre)) return true;
  return (atleta.categorias || []).some((c) => textoEsTested(c && c.division_nombre));
};

/** Agrupa la nómina en (sesión + flight), igual que el backend. */
export const agruparFlightsDeAtletas = (atletas) => {
  const grupos = {};
  (atletas || []).forEach((a) => {
    const sesion = a.sesion != null && `${a.sesion}` !== '' ? String(a.sesion) : null;
    const flight = (a.flight || '').toString().trim() || null;
    const clave = `${sesion || ''}|${flight || ''}`;
    if (!grupos[clave]) {
      grupos[clave] = {
        clave,
        sesion,
        flight,
        etiqueta: flight ? `Flight ${flight}` : 'Sin flight',
        total_atletas: 0,
        tested: 0,
      };
    }
    grupos[clave].total_atletas += 1;
    if (atletaEsTested(a)) grupos[clave].tested += 1;
  });
  return Object.values(grupos).sort((a, b) => {
    const na = Number(a.sesion);
    const nb = Number(b.sesion);
    if (a.sesion && b.sesion && !Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) {
      return na - nb;
    }
    return String(a.etiqueta).localeCompare(String(b.etiqueta), 'es');
  });
};

export const getTodosLosAtletas = async (meetId) => {
  const items = [];
  let page = 1;
  let totalPages = 1;
  do {
    const data = await getAtletas(meetId, { page, limit: 500 });
    items.push(...(data.items || []));
    totalPages = data.pagination?.total_pages || 1;
    page += 1;
  } while (page <= totalPages);
  return items;
};

export const getAtletas = async (meetId, filtros = {}) => {
  const params = new URLSearchParams();
  Object.entries(filtros).forEach(([clave, valor]) => {
    if (valor !== undefined && valor !== null && `${valor}` !== '') {
      params.set(clave, valor);
    }
  });

  const query = params.toString();
  const endpoint = API_CONFIG.ENDPOINTS.LIFTINGCAST.ATLETAS(meetId);
  const response = await apiGet(query ? `${endpoint}?${query}` : endpoint);
  const data = await leerJson(response);

  return {
    items: data.items || [],
    pagination: data.pagination || { page: 1, limit: 50, total: 0, total_pages: 0 },
  };
};

export const getDivisiones = async (meetId) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.LIFTINGCAST.DIVISIONES(meetId));
  const data = await leerJson(response);
  return data.items || [];
};

export const sincronizarMeet = async (meetId) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.LIFTINGCAST.SINCRONIZAR(meetId), {});
  const data = await leerJson(response);
  return { meet: data.data || null, mensaje: data.mensaje || 'Competencia sincronizada' };
};

export const eliminarMeet = async (meetId) => {
  const response = await apiDelete(API_CONFIG.ENDPOINTS.LIFTINGCAST.MEET(meetId));
  await leerJson(response);
};

export const getDoping = async (meetId) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.LIFTINGCAST.DOPING(meetId));
  const data = await leerJson(response);
  let flights = data.flights || [];
  if (!flights.length) {
    const atletas = await getTodosLosAtletas(meetId);
    flights = agruparFlightsDeAtletas(atletas);
  }
  return {
    doping: data.data || null,
    preview: data.preview || null,
    flights,
    meet: data.meet || null,
  };
};

export const generarDoping = async (meetId, payload = {}) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.LIFTINGCAST.DOPING(meetId), payload);
  const data = await leerJson(response);
  return {
    doping: data.data || null,
    meet: data.meet || null,
    mensaje: data.mensaje || 'Selección de doping generada',
  };
};

export const getLiftingCastDeEvento = async (eventoId) => {
  const response = await apiGet(API_CONFIG.ENDPOINTS.EVENTOS.LIFTINGCAST(eventoId));
  const data = await leerJson(response);
  return {
    evento: data.evento || null,
    meet: data.data || null,
  };
};

export const guardarUrlLiftingCastEvento = async (eventoId, url) => {
  const response = await apiPut(API_CONFIG.ENDPOINTS.EVENTOS.LIFTINGCAST(eventoId), { url });
  const data = await leerJson(response);
  return data.evento || null;
};

/** Récords que un meet puede actualizar, con la persona sugerida para cada atleta. No guarda nada. */
export const previsualizarRecordsLiftingCast = async (url) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.RECORDS.LIFTINGCAST_PREVISUALIZAR, { url });
  const data = await leerJson(response);
  return {
    meet: data.meet || null,
    candidatos: data.candidatos || [],
    sugerencias: data.sugerencias || {},
  };
};

export const buscarPersonasParaRecord = async (buscar) => {
  const params = new URLSearchParams({ buscar: buscar || '', limit: '30' });
  const response = await apiGet(`${API_CONFIG.ENDPOINTS.RECORDS.LIFTINGCAST_PERSONAS}?${params}`);
  const data = await leerJson(response);
  return data.items || [];
};

/**
 * Crea un atleta sin cuenta (queda como importado hasta que se registre).
 * Si el RUT ya existe devuelve esa persona con existente = true.
 */
export const crearAtletaParaRecord = async ({ rut, nombre, apellido, fechaNacimiento, genero }) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.RECORDS.LIFTINGCAST_PERSONAS, {
    rut,
    nombre,
    apellido,
    fecha_nacimiento: fechaNacimiento || null,
    genero: genero || null,
  });
  const data = await response.json();
  if (response.status === 409 && data.persona) {
    return { persona: data.persona, existente: true, mensaje: data.error };
  }
  if (!data || data.success === false) {
    throw new Error((data && data.error) || 'No se pudo crear el atleta');
  }
  return { persona: data.persona, existente: false };
};

/** asociaciones: [{ candidato_id, usuario_rut }] */
export const aplicarRecordsLiftingCast = async ({ url, lugar, fechaRecord, asociaciones }) => {
  const response = await apiPost(API_CONFIG.ENDPOINTS.RECORDS.LIFTINGCAST_APLICAR, {
    url,
    lugar,
    fecha_record: fechaRecord,
    asociaciones,
  });
  const data = await leerJson(response);
  return {
    creados: data.creados || [],
    omitidos: data.omitidos || [],
    mensaje: data.mensaje || 'Récords actualizados',
  };
};

export const sincronizarLiftingCastEvento = async (eventoId, url) => {
  const response = await apiPost(
    API_CONFIG.ENDPOINTS.EVENTOS.LIFTINGCAST_SINCRONIZAR(eventoId),
    { url }
  );
  const data = await leerJson(response);
  return {
    meet: data.data || null,
    evento: data.evento || null,
    mensaje: data.mensaje || 'Competencia sincronizada',
  };
};
